import Link from 'router/Link';
import PropTypes from 'prop-types';
import { colorWithOpacity } from 'utils/helpers';
import { MENU_ICON_SIZE, VERTICAL_MENU_WIDTH } from './navigationUtils';
import createComponent from 'styles/fela/createComponent';
import { reserveBoldTextWidth, withBasePadding, smallScreen } from 'styles/fela/mixins';

export const Nav = createComponent(({ theme, position }) => ({
  background: theme.header.backgroundColor,
  color: theme.header.textColor,
  boxShadow: `0 0 10px 0 ${theme.color.overlayDarkExtraFaint}`,
  clipPath: 'inset(0px -4px -10px 0px)',
  flexGrow: 1,
  flexShrink: 1,
  left: 0,
  position: 'absolute',
  top: theme.header.height,
  width: '100%',

  extend: [
    {
      condition: position === 'horizontal',
      style: {
        background: 'none',
        color: 'inherit',
        boxShadow: 'none',
        height: theme.header.height,
        paddingBottom: 0,
        paddingTop: 0,
        position: 'static',
        display: 'flex',
        alignItems: 'center',
      },
    },
  ],
}), 'nav', ['position']);

export const MenuContainer = createComponent(({
  theme,
  position = 'vertical',
}) => ({
  display: 'block',
  width: '100%',
  fontSize: theme.fontSize.small,
  extend: [
    {
      condition: position === 'horizontal',
      style: {
        display: 'flex',
        flexShrink: '3.2', // resolved IE11 issue with blocking shrinking of empty container
        justifyContent: 'flex-start',
        '> a': {
          display: 'inline-block',
          width: 'auto',
        },
      },
    },
  ],
}), 'div', ['position']);

export const MenuWrapper = createComponent(({ theme, isHidden  }) => ({
  display: 'flex',
  flexGrow: 1,
  flexWrap: 'wrap',
  position: 'relative',
  maxWidth: `calc(100% - ${theme.header.logo.width} - ${theme.margin.fine})`,
  extend: [
    {
      condition: isHidden,
      style: {
        visibility: 'hidden',
      },
    },
  ],
}), 'div', ['isHidden']);

export const LogoLink = createComponent(({ theme }) => ({
  marginRight: theme.margin.small,
  display: 'flex',
  height: theme.header.height,
  width: theme.header.logo.width,
  alignItems: 'center',
  flexShrink: 0,
  color: 'inherit',
  ':focus-visible': {
    outline: 'none',
  },
  ':focus-visible img': {
    outline: '2px solid currentColor',
    outlineOffset: '2px',
  },
}), Link);

export const LogoImage = createComponent(() => ({
  height: 'auto',
  width: 'auto',
  maxHeight: '100%',
  maxWidth: '100%',
}), 'img');


export const PersistentNavigationContainer = createComponent(({ theme, wide }) => ({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto',
  flexShrink: 0,
  fontSize: theme.fontSize.small,
  width: wide ? '30rem' : '23rem',
  extend: [
    smallScreen({
      width: 'auto',
    }),
  ],
}), 'div', ['wide']);

export const StyledPersistentNavigation = createComponent(() => ({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto',
}), 'nav');

export const Hamburger = createComponent(({ theme }) => ({
  alignSelf: 'center',
  background: 'transparent',
  border: 0,
  color: 'inherit',
  cursor: 'pointer',
  display: 'block',
  minHeight: theme.header.height,
  minWidth: MENU_ICON_SIZE,
  marginLeft: theme.margin.large,
  textAlign: 'right',
  '& svg': {
    verticalAlign: 'middle',
  },
  ':focus-visible': {
    outline: 'none',
  },
  ':focus-visible svg': {
    outline: '2px solid currentColor',
    outlineOffset: '2px',
    borderRadius: '2px',
  },
}), 'button');

export const HeaderWrapper = createComponent(({ theme, hidden }) => ({
  width: '100%',
  extend: [
    {
      condition: hidden,
      style: {
        position: 'absolute',
        visibility: 'hidden',
        pointerEvents: 'none',
        top: '-100%',

        '& div[role="menu"]': {
          display: 'none',
        }
      },
    },
  ],
}), 'div', ['hidden']);

export const HeaderContainer = createComponent(({ theme, opacity, scroll }) => ({
  color: theme.header.textColor,
  height: theme.header.height,
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: theme.zIndex.header,
  extend: [
    withBasePadding(),
  ],
  '& a': {
    WebkitTapHighlightColor: 'transparent',
  },
  '& button': {
    WebkitTapHighlightColor: 'transparent',
  },
  ':before': {
    // header gradient background before scroll
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    opacity: scroll === 'true' ? 0 : 1,
    background: `linear-gradient(${colorWithOpacity(theme.header.backgroundColor, opacity.beforeScroll.gradientStart)}, ${colorWithOpacity(theme.header.backgroundColor, opacity.beforeScroll.gradientEnd)})`,
    extend: [
      {
        // apply transition effect if before or after scroll header background has transparency
        condition: opacity.beforeScroll.gradientStart < 100
          || opacity.beforeScroll.gradientEnd < 100
          || opacity.afterScroll < 100,
        style: {
          extend: [
            {
              // do not apply transition effect if header has no gradient effect before scroll
              // and header same color before and after scroll
              condition: !(opacity.beforeScroll.gradientStart === opacity.beforeScroll.gradientEnd
                && opacity.beforeScroll.gradientEnd === opacity.afterScroll),
              style: {
                transition: `opacity 200ms ${scroll === 'true' ? 'ease-in' : 'ease-out'}`,
              },
            },
          ],
        },
      },
    ],
  },
  ':after': {
    // header gradient background after scroll
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    opacity: scroll === 'true' ? 1 : 0,
    background: `linear-gradient(${colorWithOpacity(theme.header.backgroundColor, opacity.afterScroll)}, ${colorWithOpacity(theme.header.backgroundColor, opacity.afterScroll)})`,
    extend: [
      {
        // apply transition effect if before or after scroll header background has transparency
        condition: opacity.beforeScroll.gradientStart < 100
          || opacity.beforeScroll.gradientEnd < 100
          || opacity.afterScroll < 100,
        style: {
          extend: [
            {
              // do not apply transition effect if header has no gradient effect before scroll
              // and header same color before and after scroll
              condition: !(opacity.beforeScroll.gradientStart === opacity.beforeScroll.gradientEnd
                && opacity.beforeScroll.gradientEnd === opacity.afterScroll),
              style: {
                transition: `opacity 200ms ${scroll === 'true' ? 'ease-out' : 'ease-in'}`,
              },
            },
          ],
        },
      },
    ],
  },
}), 'header', ['opacity']);

export const Content = createComponent(() => ({
  position: 'absolute',
  zIndex: 1,
  left: 0,
  top: 0,
  right: 0,
  bottom: 0,
  padding: 'inherit',
  alignItems: 'end',
  display: 'flex',
  justifyContent: 'flex-start',
  height: '100%',
}));

export const MenuLink = createComponent(({ theme, active, position }) => ({
  cursor: 'pointer',
  color: 'inherit',
  display: 'block',
  marginLeft: theme.margin.large,
  textAlign: 'center',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  ':hover': {
    fontWeight: 'bold',
  },
  ':focus-visible': {
    outlineColor: 'currentColor',
    outlineOffset: '2px',
  },
  extend: [
    reserveBoldTextWidth('data-content'),
    {
      condition: active,
      style: {
        fontWeight: 'bold',
      },
    },
    {
      condition: position === 'vertical',
      style: {
        lineHeight: 'normal',
        textAlign: 'left',
        display: 'block',
        marginLeft: 0,
        padding: '0.55rem 0',
      },
    },
  ],
}), Link, ['active', 'position']);
MenuLink.displayName = 'MenuLink';
MenuLink.propTypes = {
  active: PropTypes.bool,
  position: PropTypes.string,
};

export const HeaderSpacer = createComponent(({ theme }) => ({
  width: '100%',
  height: theme.header.height,
  background: theme.color.page,
}));

export const MenuNavigationWrapper = createComponent(({ hidden }) => ({
  maxWidth: '100%',
  extend: [
    {
      condition: hidden,
      style: {
        position: 'absolute',
        visibility: 'hidden',
        pointerEvents: 'none',
        left: 0,
        top: 0,
        width: 'auto',
      },
    },
  ],
}), 'div', ['hidden']);

export const SignUpButtonWrapper = createComponent(({ hidden }) => ({
  extend: [{
    condition: hidden,
    style: {
      position: 'fixed',
      left: '-9999px',
      visibility: 'hidden',
      pointerEvents: 'none',
    },
  }],
}), 'span', ['hidden']);
SignUpButtonWrapper.displayName = 'SignUpButtonWrapper';

export const SignUpButton = createComponent(({ theme, position }) => ({
  cursor: 'pointer',
  color: 'inherit',
  display: 'inline-block',
  textAlign: 'center',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  border: '2px solid currentColor',
  fontSize: theme.fontSize.small,
  borderRadius: theme.radius.default,
  padding: `${theme.margin.fine} ${theme.margin.medium}`,
  marginLeft: theme.margin.large,
  transition: 'background-color 200ms ease-out',
  ':hover': {
    backgroundColor: colorWithOpacity(theme.color.primary, 20),
  },
  ':focus-visible': {
    outlineColor: 'currentColor',
    outlineOffset: '2px',
  },
  extend: [
    {
      condition: position === 'vertical',
      style: {
        lineHeight: 'normal',
        marginLeft: 0,
        marginTop: theme.margin.medium,
        marginBottom: theme.margin.fine,
        textAlign: 'center',
        display: 'block',
        width: '100%',
      },
    },
  ],
}), Link, ['active', 'position']);
SignUpButton.displayName = 'SignUpButton';

export const NavigationOverlay = createComponent(({ theme, isVisible }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.color.overlayModal,
  zIndex: theme.zIndex.navigationOverlay,
  opacity: isVisible ? 1 : 0,
  pointerEvents: isVisible ? 'auto' : 'none',
  transition: 'opacity 200ms ease-out 200ms',
}), 'div', ['isVisible']);

export const StyledSearchLink = createComponent(({ theme }) => ({
  display: 'flex !important',
  flexDirection: 'column',
  alignItems: 'flex-end',
  justifyContent: 'center',
  minHeight: theme.header.height,
  marginLeft: '0',
  ':focus-visible': {
    outline: 'none',
  },
  ':focus-visible svg': {
    outline: '2px solid currentColor',
    outlineOffset: '2px',
    borderRadius: '2px',
  },
}), MenuLink);

export const StyledProfileAvatar = createComponent(({ theme }) => ({
  display: 'flex !important',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: theme.header.height,
  cursor: 'pointer',
  marginLeft: theme.margin.large,
  ':focus-visible': {
    outline: 'none',
  },
  ':focus-visible > div': {
    outline: '2px solid currentColor',
    outlineOffset: '2px',
  },
}), 'div');

export const DropdownWrapper = createComponent(() => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
}));

export const MenuDropdownBridge = createComponent(() => ({
  position: 'absolute',
  top: '-0.9375rem',
  left: 0,
  right: 0,
  height: '0.9375rem',
}));

export const MenuDropdownStyled = createComponent(({ theme, isOpen }) => ({
  position: 'absolute',
  top: '100%',
  right: '-35px',
  background: theme.color.page,
  width: VERTICAL_MENU_WIDTH,
  border: `1px solid ${theme.color.secondaryFullBackground}`,
  borderRadius: theme.radius.default,
  fontSize: theme.fontSize.small,
  boxShadow: `0 6px 30px 2px ${theme.color.tooltipShadow}`,
  padding: `${theme.margin.medium} ${theme.margin.large}`,
  lineHeight: '1.4',
  display: 'flex',
  flexDirection: 'column',
  zIndex: theme.zIndex.navigationMenu,
  marginTop: '-10px',
  opacity: isOpen ? 1 : 0,
  pointerEvents: isOpen ? 'auto' : 'none',
  transform: isOpen ? 'translateY(0)' : 'translateY(-4px)',
  transition: 'opacity 200ms ease-out 200ms, transform 200ms ease-out 200ms',
  color: theme.color.primary,
  visibility: 'hidden',
  ':before': {
    content: '""',
    position: 'absolute',
    top: '-7px',
    right: '35px',
    width: 0,
    height: 0,
    borderLeft: '12px solid transparent',
    borderRight: '12px solid transparent',
    borderBottom: `7px solid ${theme.color.secondaryFullBackground}`,
  },
  ':after': {
    content: '""',
    position: 'absolute',
    top: '-6px',
    right: '37px',
    width: 0,
    height: 0,
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderBottom: `6px solid ${theme.color.page}`,
  },
  extend: [
    smallScreen({
      right: '-11px',
      ':before': {
        right: '11px',
      },
      ':after': {
        right: '13px',
      },
    }),
  ],
}), 'div', ['isOpen']);

export const ProfileMenuLink = createComponent(({ active }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  padding: '0.4rem 0',
  marginLeft: 0,
  lineHeight: 1.4,
  textAlign: 'left',
  ':hover': {
    fontWeight: 'bold',
  },
  extend: [
    {
      condition: active,
      style: {
        fontWeight: 'bold',
      },
    },
  ],
}), MenuLink, ['active']);

ProfileMenuLink.propTypes = {
  active: PropTypes.bool,
};
